import {DateTime} from "luxon";
import {
    isFullMonthRange,
    isFullQuarterRange,
    isFullYearRange,
} from "../../../general/DateRangeInput/utils/dateRangeUtils.ts";

export enum ComparisonMode {
    None = "none",
    PreviousPeriod = "previous_period",
    SamePeriodLastYear = "same_period_last_year",
}

export interface DateRangeStrings {
    from: string;
    to: string;
}

const FORMAT = "yyyy-MM-dd";

function parse(value: string): DateTime {
    return DateTime.fromFormat(value, FORMAT);
}

function formatRange(from: DateTime, to: DateTime): DateRangeStrings {
    return {from: from.toFormat(FORMAT), to: to.toFormat(FORMAT)};
}

function previousPeriod(range: DateRangeStrings): DateRangeStrings {
    const from = parse(range.from);
    const to = parse(range.to);

    if (isFullMonthRange(from, to)) {
        const prev = from.minus({months: 1}).startOf("month");
        return formatRange(prev, prev.endOf("month"));
    }

    if (isFullQuarterRange(from, to)) {
        const prev = from.minus({quarters: 1}).startOf("quarter");
        return formatRange(prev, prev.endOf("quarter"));
    }

    if (isFullYearRange(from, to)) {
        const prev = from.minus({years: 1});
        return formatRange(prev.startOf("year"), prev.endOf("year"));
    }

    const lengthDays = to.diff(from, "days").days + 1;
    const previousTo = from.minus({days: 1});
    const previousFrom = previousTo.minus({days: lengthDays - 1});
    return formatRange(previousFrom, previousTo);
}

function samePeriodLastYear(range: DateRangeStrings): DateRangeStrings {
    const from = parse(range.from);
    const to = parse(range.to);
    const shiftedFrom = from.minus({years: 1});

    if (isFullMonthRange(from, to)) {
        return formatRange(shiftedFrom.startOf("month"), shiftedFrom.endOf("month"));
    }

    if (isFullQuarterRange(from, to)) {
        return formatRange(shiftedFrom.startOf("quarter"), shiftedFrom.endOf("quarter"));
    }

    if (isFullYearRange(from, to)) {
        return formatRange(shiftedFrom.startOf("year"), shiftedFrom.endOf("year"));
    }

    return formatRange(shiftedFrom, to.minus({years: 1}));
}

export function resolveComparison(
    mode: ComparisonMode,
    currentRange: DateRangeStrings,
): DateRangeStrings | null {
    if (mode === ComparisonMode.PreviousPeriod) return previousPeriod(currentRange);
    if (mode === ComparisonMode.SamePeriodLastYear) return samePeriodLastYear(currentRange);
    return null;
}

export function serializeComparisonPeriod(
    mode: ComparisonMode,
    currentRange: DateRangeStrings,
): string | undefined {
    const resolved = resolveComparison(mode, currentRange);
    return resolved ? `${resolved.from}:${resolved.to}` : undefined;
}
